import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Typography,
  Chip,
  Divider,
  Stack,
} from '@mui/material';
import {
  Sync as SyncIcon,
  Check as CheckIcon,
  SwapHoriz as SwapHorizIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface DemarcheSyncMigrationModalProps {
  open: boolean;
  onClose: () => void;
  demarcheId: number;
  isCompteTemporaire: boolean;
  onSuccess: () => void;
}

const DemarcheSyncMigrationModal: React.FC<DemarcheSyncMigrationModalProps> = ({
  open,
  onClose,
  demarcheId,
  isCompteTemporaire,
  onSuccess,
}) => {
  const [numeroAgdref, setNumeroAgdref] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleSync = async () => {
    // Validation
    if (isCompteTemporaire && (!numeroAgdref || !username || !password)) {
      setError('Pour un compte temporaire, vous devez fournir le numéro AGDREF, le nom d\'utilisateur et le mot de passe');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);
    setResult(null);

    try {
      const payload: any = {};

      if (isCompteTemporaire) {
        // Migration de compte temporaire vers définitif
        payload.numero_agdref = numeroAgdref;
        payload.username = username;
        payload.password = password;
      } else if (username && password) {
        // Synchronisation avec nouveaux identifiants
        payload.username = username;
        payload.password = password;
      }
      // Si aucun identifiant fourni, utilise ceux enregistrés

      const response = await api.post(`/demarches/${demarcheId}/sync-anef`, payload);

      if (response.data.success) {
        setSuccess(true);
        setResult(response.data.data);
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 2000);
      } else {
        setError(response.data.message || 'Erreur lors de la synchronisation');
      }
    } catch (err: any) {
      console.error('Erreur sync/migration:', err);
      setError(
        err.response?.data?.message ||
        err.message ||
        'Erreur lors de la synchronisation/migration'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNumeroAgdref('');
    setUsername('');
    setPassword('');
    setError('');
    setSuccess(false);
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isCompteTemporaire ? (
            <>
              <SwapHorizIcon />
              <Typography variant="h6">Migrer vers compte définitif</Typography>
            </>
          ) : (
            <>
              <SyncIcon />
              <Typography variant="h6">Synchroniser la démarche ANEF</Typography>
            </>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && result && (
          <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
            {result.migrated ? (
              <>
                Migration réussie ! Le compte temporaire a été migré vers un compte définitif
                et la démarche a été marquée comme terminée.
              </>
            ) : (
              <>
                Synchronisation réussie ! La démarche a été mise à jour avec les dernières données ANEF.
              </>
            )}
          </Alert>
        )}

        {isCompteTemporaire && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Ce client possède un compte temporaire
            </Typography>
            <Typography variant="body2">
              Pour migrer vers un compte définitif, vous devez avoir reçu votre numéro AGDREF.
              La migration mettra à jour toutes les informations du client et marquera la démarche comme terminée.
            </Typography>
          </Alert>
        )}

        {!success && (
          <Box>
            {isCompteTemporaire && (
              <TextField
                fullWidth
                label="Numéro AGDREF"
                value={numeroAgdref}
                onChange={(e) => setNumeroAgdref(e.target.value)}
                disabled={loading}
                sx={{ mb: 2 }}
                autoFocus
                helperText="Le numéro AGDREF à 10 chiffres que vous avez reçu"
              />
            )}

            <TextField
              fullWidth
              label={isCompteTemporaire ? "Nom d'utilisateur ANEF (Numéro AGDREF)" : "Nom d'utilisateur ANEF (optionnel)"}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              sx={{ mb: 2 }}
              helperText={isCompteTemporaire
                ? "Votre numéro AGDREF"
                : "Laissez vide pour utiliser les identifiants enregistrés"}
            />

            <TextField
              fullWidth
              type="password"
              label={isCompteTemporaire ? "Mot de passe ANEF" : "Mot de passe ANEF (optionnel)"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              helperText={isCompteTemporaire
                ? "Le mot de passe de votre compte définitif"
                : "Laissez vide pour utiliser le mot de passe enregistré"}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSync();
                }
              }}
            />

            {!isCompteTemporaire && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Si vous ne fournissez pas d'identifiants, les identifiants ANEF enregistrés
                seront utilisés pour la synchronisation.
              </Alert>
            )}
          </Box>
        )}

        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, mt: 2 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {isCompteTemporaire ? 'Migration en cours...' : 'Synchronisation en cours...'}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {success ? 'Fermer' : 'Annuler'}
        </Button>

        {!success && (
          <Button
            variant="contained"
            onClick={handleSync}
            disabled={loading || (isCompteTemporaire && (!numeroAgdref || !username || !password))}
            startIcon={loading ? <CircularProgress size={20} /> : isCompteTemporaire ? <SwapHorizIcon /> : <SyncIcon />}
            color={isCompteTemporaire ? 'warning' : 'primary'}
          >
            {loading
              ? (isCompteTemporaire ? 'Migration...' : 'Synchronisation...')
              : (isCompteTemporaire ? 'Migrer vers compte définitif' : 'Synchroniser')
            }
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DemarcheSyncMigrationModal;
